class SilhouetteGrid extends SilhouetteBase {
  constructor(section) {
    super(section);
    this.section = section;
    this.sidebars = document.querySelectorAll(".silhouette-see-all__sidebar");
    this.overlay = document.querySelector(".silhouette-see-all__overlay");
    this.closeButtons = document.querySelectorAll("[data-sidebar-close]");

    // Wait for GSAP to load before initializing
    if (typeof gsap !== "undefined") {
      this.initGSAP();
    } else {
      window.addEventListener("load", () => this.initGSAP());
    }
  }

  initGSAP() {
    if (typeof gsap === "undefined" || typeof ScrollTrigger === "undefined")
      return;

    // Set z-index for current and next sections
    gsap.set(this.section, {
      position: "relative",
      zIndex: 0,
      overflow: "clip",
    });

    // Pin the section when its bottom reaches the bottom of the viewport and manter pin indefinidamente
    ScrollTrigger.create({
      trigger: this.section,
      start: "bottom bottom",
      end: "bottom+=1",
      pin: true,
      pinSpacing: false,
      markers: false,
    });

    // Sync the translateY of the gallery-wrapper with the offset of the next section
    const galleryWrapper = this.section.querySelector(
      ".silhouette-grid__gallery-wrapper"
    );
    const nextSection = this.section.closest(".silhouette-section")
      ? this.section.closest(".silhouette-section").nextElementSibling
      : this.section.nextElementSibling;
    if (galleryWrapper && nextSection) {
      // Function to create the ScrollTrigger for the animation
      const createGalleryScrollTrigger = () => {
        // Remove old triggers
        if (galleryWrapper._scrollTriggerInstance) {
          galleryWrapper._scrollTriggerInstance.kill();
          galleryWrapper._scrollTriggerInstance = null;
        }
        galleryWrapper.style.transform = "";
        // Create new trigger
        galleryWrapper._scrollTriggerInstance = ScrollTrigger.create({
          trigger: this.section,
          start: "bottom bottom",
          end: "bottom+=1",
          scrub: true,
          onUpdate: () => {
            const nextRect = nextSection.getBoundingClientRect();
            const sectionRect = this.section.getBoundingClientRect();
            const deltaY = Math.max(0, sectionRect.bottom - nextRect.top);
            galleryWrapper.style.transform = `translateY(-${deltaY}px)`;
          },
          markers: false,
        });
      };
      // Create initial trigger
      createGalleryScrollTrigger();

      // Remove old listener if it exists
      if (galleryWrapper._resizeListener) {
        window.removeEventListener("resize", galleryWrapper._resizeListener);
      }
      // Add new listener
      galleryWrapper._resizeListener = () => {
        createGalleryScrollTrigger();
      };
      window.addEventListener("resize", galleryWrapper._resizeListener);
    }

    // Add a class to indicate that the animation is active
    this.section.classList.add("silhouette-grid--animated");

    // Add responsive resize handler
    this.resizeObserver = this.handleResize.bind(this);
    window.addEventListener("resize", this.resizeObserver);

    // No need to store animation instances
    this.animations = {};
  }

  handleResize() {
    // Debounce implementation
    clearTimeout(this.resizeTimeout);
    this.resizeTimeout = setTimeout(() => {
      // Check if device type changed (mobile vs desktop)
      const wasMobile = this.isMobile;
      this.isMobile = window.innerWidth < 768;

      // Only refresh if device type changed (crossing breakpoint)
      if (wasMobile !== this.isMobile) {
        // Kill existing animations
        if (this.animations) {
          if (this.animations.pinTrigger) this.animations.pinTrigger.kill();
          if (this.animations.galleryTrigger)
            this.animations.galleryTrigger.kill();
        }

        // Re-initialize GSAP animations with new responsive values
        this.initGSAP();
      }
    }, 250); // Wait for resize to finish
  }

  // Clean up event listeners when section is removed
  destroy() {
    if (this.resizeObserver) {
      window.removeEventListener("resize", this.resizeObserver);
    }

    // Kill any active animations
    if (this.animations) {
      if (this.animations.pinTrigger) this.animations.pinTrigger.kill();
      if (this.animations.galleryTrigger) this.animations.galleryTrigger.kill();
    }
  }

  init() {
    if (!this.section) return;
    this.bindEvents();
  }

  bindEvents() {
    this.section.addEventListener("click", () => this.handleSectionClick());
    this.section.addEventListener("keydown", (e) =>
      this.handleSectionKeydown(e)
    );
    this.closeButtons.forEach((button) => {
      button.addEventListener("click", () => this.closeAllSidebars());
    });
    this.overlay.addEventListener("click", () => this.closeAllSidebars());
    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape") this.closeAllSidebars();
    });
  }

  handleSectionClick() {
    const name = this.section.getAttribute("data-silhouette-name");
    if (name) this.openSidebar(name);
  }

  handleSectionKeydown(e) {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      const name = this.section.getAttribute("data-silhouette-name");
      if (name) this.openSidebar(name);
    }
  }

  openSidebar(name) {
    this.closeAllSidebars();
    const sidebar = this.findSidebarByName(name);
    if (sidebar) {
      this.activateSidebar(sidebar);
    }
  }

  findSidebarByName(name) {
    return Array.from(this.sidebars).find((sb) => {
      const title = sb.querySelector(
        ".silhouette-see-all__sidebar-title"
      )?.textContent;
      return title && title.toLowerCase() === name.toLowerCase();
    });
  }

  activateSidebar(sidebar) {
    sidebar.classList.add("is-active");
    this.overlay.classList.add("is-active");
    document.body.style.overflow = "hidden";
    sidebar.setAttribute("aria-hidden", "false");
    sidebar.focus();
  }

  closeAllSidebars() {
    this.sidebars.forEach((sb) => sb.classList.remove("is-active"));
    this.overlay.classList.remove("is-active");
    document.body.style.overflow = "";
  }
}

// Initialize all instances of the section
window.addEventListener("load", () => {
  document.querySelectorAll(".silhouette-grid").forEach((section) => {
    if (!section.__silhouetteGridInstance) {
      section.__silhouetteGridInstance = new SilhouetteGrid(section);
      section.__silhouetteGridInstance.init();
    }
  });
});
